import { useCallback, useEffect } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useLocation } from 'react-router-dom';

import { getAccessToken, handleUnauthorized } from '@/lib/api/axios';
import { chatsApi } from '@/lib/api/chats.api';
import { getEnv } from '@/lib/env';
import { useChatStore } from '@/stores/chat.store';

import type {
  ChatListResponse,
  ChatMessagesResponse,
  ChatQuery,
  SSEEvent,
} from '@repo/shared-types';
import type { UseQueryOptions } from '@tanstack/react-query';

export function useChats(
  query?: Partial<ChatQuery>,
  options?: Omit<UseQueryOptions<ChatListResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: ['chats', query],
    queryFn: () => chatsApi.listChats(query),
    ...options,
  });
}

export function useChatMessages(
  chatId: string | null,
  options?: Omit<UseQueryOptions<ChatMessagesResponse>, 'queryKey' | 'queryFn'>,
) {
  return useQuery({
    queryKey: ['chats', chatId, 'messages'],
    queryFn: () => chatsApi.getChatMessages(chatId!),
    enabled: !!chatId,
    ...options,
  });
}

export function useDeleteChat() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const { startNewChat } = useChatStore();

  return useMutation({
    mutationFn: (chatId: string) => chatsApi.deleteChat(chatId),
    onMutate: async (chatIdToDelete) => {
      // Cancel any outgoing refetches for this chat's messages to prevent race condition
      await queryClient.cancelQueries({
        queryKey: ['chats', chatIdToDelete, 'messages'],
      });

      // If deleting the current chat, clear it and navigate away
      if (location.pathname.includes(`/chatbot/${chatIdToDelete}`)) {
        startNewChat();
        navigate('/chatbot');
      }
    },
    onSuccess: (_, deletedChatId) => {
      // Remove the deleted chat's messages from cache
      queryClient.removeQueries({
        queryKey: ['chats', deletedChatId, 'messages'],
      });
      // Invalidate the chat list to refresh
      queryClient.invalidateQueries({ queryKey: ['chats'] });
    },
  });
}

export function useSendMessage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    addUserMessage,
    handleSSEEvent,
    finishStreaming,
    clearStreamingState,
    setAbortController,
    cancelStreaming,
  } = useChatStore();

  // Cleanup: abort any in-flight stream when component unmounts
  useEffect(() => {
    return () => {
      const controller = useChatStore.getState().abortController;
      controller?.abort();
    };
  }, []);

  const sendMessage = useCallback(
    async (message: string, isRetry = false) => {
      const baseUrl = getEnv('VITE_API_URL');
      const token = getAccessToken();

      // Abort any previous in-flight stream
      const prevController = useChatStore.getState().abortController;
      prevController?.abort();
      const newController = new AbortController();
      setAbortController(newController);
      const { signal } = newController;

      // Add user message to UI immediately
      addUserMessage(message);

      // Get current chat ID from store at call time to avoid stale closure
      const chatId = useChatStore.getState().currentChatId;

      try {
        const response = await fetch(`${baseUrl}/chats/send-message-stream`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            message,
            chat_id: chatId || undefined,
          }),
          signal,
        });

        // Handle 401 unauthorized
        if (response.status === 401) {
          // Only attempt refresh once to prevent infinite recursion
          if (!isRetry) {
            const refreshed = await handleUnauthorized();
            if (refreshed) {
              // Retry with new token
              clearStreamingState();
              return sendMessage(message, true);
            }
          }
          throw new Error('Session expired. Please log in again.');
        }

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || `HTTP error ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error('No response body');
        }

        const decoder = new TextDecoder();
        let buffer = '';

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });

          // Process complete SSE lines
          const lines = buffer.split('\n');
          buffer = lines.pop() || ''; // Keep incomplete line in buffer

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const dataStr = line.slice(6).trim();
              if (dataStr) {
                try {
                  const event = JSON.parse(dataStr) as SSEEvent;
                  handleSSEEvent(event);

                  // Handle done event to finish streaming
                  if (event.type === 'done') {
                    finishStreaming(event.chat_id);

                    // Navigate to new chat URL if this was a new chat
                    const isNewChat = !location.pathname.includes('/chatbot/');
                    if (isNewChat) {
                      navigate(`/chatbot/${event.chat_id}`, { replace: true });
                    }

                    // Invalidate chats list to show new/updated chat
                    queryClient.invalidateQueries({ queryKey: ['chats'] });
                  }
                } catch {
                  console.error('Failed to parse SSE data:', dataStr);
                }
              }
            }
          }
        }
      } catch (error) {
        // Ignore abort errors (expected when switching chats)
        if (error instanceof Error && error.name === 'AbortError') {
          return;
        }
        // Handle error by creating an error message
        handleSSEEvent({
          type: 'error',
          message: error instanceof Error ? error.message : 'Failed to send message',
        });
        clearStreamingState();
      }
    },
    [
      addUserMessage,
      handleSSEEvent,
      finishStreaming,
      clearStreamingState,
      setAbortController,
      queryClient,
      navigate,
      location.pathname,
    ],
  );

  return { sendMessage, abortStream: cancelStreaming };
}
